/**
 * Client-portal document category buckets.
 *
 * 7/26 E — the portal groups documents into a FIXED set of client-facing buckets
 * (Tax Bills / Value Notices / Operating Data / Other). There is no separate
 * "portal document types" registry any more, and the portal upload has no type
 * dropdown: the client just uploads a file, it hits the firm's document review
 * queue, and the firm assigns the correct type from the single master
 * document-types list. The backend (client-portal.js categorizeDocType) maps that
 * assigned type into one of the buckets below for the filter chips.
 */

export const DEFAULT_PORTAL_DOC_CATEGORIES = ['Tax Bills', 'Value Notices', 'Operating Data'] as const;

/**
 * The catch-all bucket. categorizeDocType() files any document whose type doesn't
 * match a known bucket under 'Other' (DOC_CATEGORY.OTHER in
 * src/routes/client-portal.js), and portal uploads arrive as 'Other' until the firm
 * types them at review — so it's always offered in the filter chips.
 */
export const OTHER_PORTAL_DOC_CATEGORY = 'Other';

/**
 * Ensure the catch-all "Other" bucket is present, appended once (case-insensitive)
 * if the configured portal categories omit it. Idempotent. Used for both the
 * upload type dropdown and the filter chips so they stay consistent.
 */
export function withOtherCategory(categories: readonly string[]): string[] {
  const hasOther = categories.some(
    (c) => c.trim().toLowerCase() === OTHER_PORTAL_DOC_CATEGORY.toLowerCase(),
  );
  return hasOther ? [...categories] : [...categories, OTHER_PORTAL_DOC_CATEGORY];
}

/** The Documents-section filter chips: "All" plus the portal categories (with "Other" always offered). */
export function filterCategories(categories: readonly string[]): string[] {
  return ['All', ...withOtherCategory(categories)];
}
